import { useEffect, useMemo, useRef, useState } from "react";
import { endpoints } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { RotateCcw, Download, FileSpreadsheet, Loader2, Sparkles, Zap } from "lucide-react";
import axios from "axios";

// ---------------------------------------------------------------------------
// ProbabilisticPanel
// A P10 / P50 / P90 + funnel (Demand only) + 2-tab slider deck + agent
// narrative that mounts below the Demand / Refresh / Recovery workbenches.
// Shared slider state is lifted into Lifecycle.jsx so edits cascade across
// agents.
// ---------------------------------------------------------------------------

const AGENT_LABEL = {
  plan:    { title: "Workday Joiner Forecast",  unit: "joiners",       agent: "demand"   },
  refresh: { title: "Refresh Forecast",         unit: "refresh units", agent: "refresh"  },
  recover: { title: "Recovery Forecast",        unit: "returns due",   agent: "recovery" },
};

// Per-stage slider sets are defined on SLIDER_META[].agents below.
// Demand → HR-side knobs; Refresh → device-side; Recovery → returns.

const SLIDER_META = {
  // ─── Demand-side (HR / business) ───
  hiring_confidence: { tab: "business", agents: ["plan"],              label: "Hiring Confidence", unit: "%", min: 40, max: 99, step: 1,   bl: 82 },
  buffer:            { tab: "business", agents: ["plan"],              label: "Buffer",            unit: "%", min: 0,  max: 20, step: 0.5, bl: 8  },
  cancellation:      { tab: "business", agents: ["plan"],              label: "Cancellation",      unit: "%", min: 0,  max: 20, step: 0.5, bl: 5  },
  // ─── Refresh-side (device / fleet) ───
  dex_risk_threshold:   { tab: "business", agents: ["refresh"], label: "DEX Risk Threshold", unit: "",    min: 40, max: 85,  step: 1, bl: 60   },
  warranty_window_days: { tab: "business", agents: ["refresh"], label: "Warranty Window",    unit: "d",   min: 30, max: 180, step: 5, bl: 90   },
  incident_density:     { tab: "business", agents: ["refresh"], label: "Incident Density",   unit: "/mo", min: 0.5,max: 8,   step: 0.1, bl: 2.5 },
  battery_floor:        { tab: "business", agents: ["refresh"], label: "Battery Floor",      unit: "%",   min: 40, max: 90,  step: 1, bl: 70   },
  // ─── Recovery-side ───
  reuse_rate:      { tab: "business", agents: ["recover"], label: "Reuse Rate",      unit: "%", min: 0,  max: 70, step: 1, bl: 32 },
  collection_rate: { tab: "business", agents: ["recover"], label: "Collection Rate", unit: "%", min: 70, max: 100,step: 1, bl: 91 },
  wipe_success:    { tab: "business", agents: ["recover"], label: "Wipe Success",    unit: "%", min: 70, max: 100,step: 1, bl: 85 },
  // ─── Historical (shared / selective) ───
  attrition:      { tab: "historical", agents: ["plan","recover"],              label: "Attrition",          unit: "%",   min: 4,   max: 24,  step: 0.5, bl: 11   },
  break_fix:      { tab: "historical", agents: ["refresh"],                     label: "Break-fix",          unit: "%",   min: 2,   max: 18,  step: 0.5, bl: 6    },
  fleet_age_mean: { tab: "historical", agents: ["refresh"],                     label: "Fleet Age Mean",     unit: "mo",  min: 24,  max: 60,  step: 1,   bl: 38   },
  seasonality_q2: { tab: "historical", agents: ["plan","recover"],              label: "Seasonality · Q2",   unit: "×",   min: 0.8, max: 1.5, step: 0.01, bl: 1.22 },
  seasonality_q1: { tab: "historical", agents: ["plan"],                        label: "Seasonality · Q1",   unit: "×",   min: 0.7, max: 1.3, step: 0.01, bl: 0.92 },
  seasonality_q3: { tab: "historical", agents: ["plan"],                        label: "Seasonality · Q3",   unit: "×",   min: 0.6, max: 1.3, step: 0.01, bl: 0.83 },
  growth:         { tab: "historical", agents: ["plan","refresh","recover"],    label: "Growth",             unit: "%",   min: -5,  max: 15,  step: 0.5, bl: 4    },
};

// Debounce helper
function useDebounced(value, delay) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

// ============================================================================
// Public component
// ============================================================================
export function ProbabilisticPanel({ stage, brandBlue, brandSoft, sliders, onSlidersChange, onReset, onForecastUpdate, embedded = false, agentRunning = false, agentSettled = false, collectReady = false, reasoningStarted = false, onRunReasoning }) {
  const meta = AGENT_LABEL[stage];

  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [narrative, setNarrative] = useState("");
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [sheetDrawerOpen, setSheetDrawerOpen] = useState(false);
  const [drillState, setDrillState] = useState(null); // { stage, data }
  const [counts, setCounts] = useState(null);
  const [dataSources, setDataSources] = useState(null);
  const prevSlidersJson = useRef("");
  const debouncedSliders = useDebounced(sliders, 160);
  // Resampling ticker: brief "running Monte Carlo…" indicator shown each time
  // the sliders commit (not on first mount). Lets execs feel the recompute.
  const [resampling, setResampling] = useState(false);
  const firstForecast = useRef(true);
  // Agent-run pulse: triggered the moment Run agent is clicked so the chart
  // visibly participates in the lifecycle, not just slider edits.
  const [consumedKey, setConsumedKey] = useState(0);
  const prevRunning = useRef(false);
  useEffect(() => {
    if (agentRunning && !prevRunning.current) {
      // Rising edge: agent just started — show the resampling ticker so the
      // Plan step reads "the agent is consuming the forecast right now".
      setResampling(true);
      setTimeout(() => setResampling(false), 1200);
    }
    if (!agentRunning && prevRunning.current && agentSettled) {
      // Falling edge after settle: pulse the P50 card to signal "consumed".
      setConsumedKey((k) => k + 1);
    }
    prevRunning.current = agentRunning;
  }, [agentRunning, agentSettled]);

  // Load row counts + connector feed status once.
  useEffect(() => {
    endpoints.probExcelCounts().then(setCounts).catch(() => {});
    endpoints.probDataSources().then(setDataSources).catch(() => {});
  }, []);

  // Pull a forecast whenever sliders change (debounced).
  useEffect(() => {
    let alive = true;
    setLoading(true);
    // Only play the resampling ticker on slider commits (not initial mount).
    if (!firstForecast.current) setResampling(true);
    endpoints.probForecast(meta.agent, debouncedSliders).then((d) => {
      if (!alive) return;
      setForecast(d);
      setLoading(false);
      if (onForecastUpdate) onForecastUpdate(d);
      firstForecast.current = false;
      // Hold the ticker for ~500 ms so the eye catches it.
      setTimeout(() => alive && setResampling(false), 500);
    }).catch(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.agent, JSON.stringify(debouncedSliders)]);

  // Chart unlock is driven by the parent workbench. The sources auto-connect
  // when Collect finishes (collectReady=true); the chart + reasoning unlock
  // when the user clicks "Run agent" inside this Plan step (reasoningStarted).
  // Clicking the Run agent CTA calls back to the parent to actually start
  // Phase 2–5. If no onRunReasoning handler is wired (legacy / preview), fall
  // back to just unlocking visually.
  const connected = reasoningStarted;
  const [showConnected, setShowConnected] = useState(false);
  useEffect(() => { setShowConnected(false); }, [stage]);
  const handleConnect = () => {
    if (onRunReasoning) {
      onRunReasoning();
    } else {
      setShowConnected(true);
    }
  };
  const effectiveConnected = connected || showConnected;

  // Narrative — only re-request when sliders commit (debounced further).
  const narrativeKey = useMemo(() => JSON.stringify(debouncedSliders), [debouncedSliders]);
  useEffect(() => {
    if (prevSlidersJson.current === narrativeKey) return;
    prevSlidersJson.current = narrativeKey;
    setNarrativeLoading(true);
    endpoints.probNarrative({ agent: meta.agent, sliders: debouncedSliders })
      .then((d) => setNarrative(d.narrative || ""))
      .catch(() => setNarrative(""))
      .finally(() => setNarrativeLoading(false));
  }, [meta.agent, narrativeKey]);

  const openDrill = async (funnelStage) => {
    try {
      const data = await endpoints.probFunnelDrill(funnelStage, 20);
      setDrillState({ stage: funnelStage, data });
    } catch { /* noop */ }
  };

  const onBarChange = (key, newValue) => {
    const next = { ...sliders, [key]: newValue };
    onSlidersChange(next);
  };

  // "Dirty" if any slider deviates from baseline.
  const dirty = useMemo(() => {
    return Object.keys(SLIDER_META).some((k) => {
      if (sliders[k] == null) return false;
      return Math.abs(Number(sliders[k]) - SLIDER_META[k].bl) > 0.001;
    });
  }, [sliders]);

  return (
    <div
      className={embedded ? "" : "rounded-2xl bg-white p-6 md:p-7 mb-10"}
      style={embedded ? {} : { border: `1px solid ${brandSoft}`, boxShadow: `0 1px 0 ${brandSoft}` }}
      data-testid={`prob-panel-${stage}`}
    >
      {/* Header — hidden when embedded (the Step shell provides its own) */}
      {!embedded && (
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h3 className="text-xl font-semibold text-slate-900">{meta.title}</h3>
          <p className="text-slate-500 mt-1 text-[13px]">
            {stage === "plan"
              ? "Planned → Confidence → Policy → Cancellations → Net. Drag any lever to recompute the range in real time."
              : "P10 / P50 / P90 with distribution — bootstrapped from the shipped Chevron lifecycle history."}
          </p>
        </div>
        {dirty && (
          <Button
            data-testid={`prob-reset-${stage}`}
            variant="outline"
            onClick={onReset}
            className="h-9 px-3 border-slate-200 text-xs"
          >
            <RotateCcw size={11} className="mr-1.5" />
            Reset to baseline
          </Button>
        )}
      </div>
      )}

      {/* Reset button when embedded (header is hidden) */}
      {embedded && dirty && (
        <div className="flex justify-end mb-3">
          <Button
            data-testid={`prob-reset-${stage}`}
            variant="outline"
            onClick={onReset}
            className="h-9 px-3 border-slate-200 text-xs"
          >
            <RotateCcw size={11} className="mr-1.5" />
            Reset to baseline
          </Button>
        </div>
      )}

      {/* Live data-source feed strip */}
      <DataSourcesStrip
        key={`strip-${stage}-${effectiveConnected ? "on" : "off"}`}  // re-mount on connect so pulse-in replays
        sources={dataSources}
        counts={counts}
        onOpen={() => setSheetDrawerOpen(true)}
        resampling={resampling}
        connected={effectiveConnected}
        collectReady={collectReady}
        onConnect={handleConnect}
        brandBlue={brandBlue}
        brandSoft={brandSoft}
      />

      {/* Freshness line — only after sources are connected */}
      {effectiveConnected && (
        <FreshnessLine
          sources={dataSources}
          counts={counts}
          paths={forecast?.n_paths || 10000}
          brandBlue={brandBlue}
        />
      )}

      {!effectiveConnected ? (
        <ConnectSourcesPlaceholder
          onConnect={handleConnect}
          collectReady={collectReady}
          brandBlue={brandBlue}
          brandSoft={brandSoft}
        />
      ) : (
      <div className={`grid grid-cols-1 lg:grid-cols-12 gap-5 mt-5`}>
        {/* ----- Left column: funnel / headline + distribution ----- */}
        <div className="lg:col-span-7 space-y-4">
          {stage === "plan" ? (
            <DemandFunnel forecast={forecast} loading={loading} onDrill={openDrill} brandBlue={brandBlue} brandSoft={brandSoft} agentRunning={agentRunning} consumedKey={consumedKey} />
          ) : (
            <HeadlineForecast forecast={forecast} loading={loading} unit={meta.unit} brandBlue={brandBlue} brandSoft={brandSoft} agentRunning={agentRunning} consumedKey={consumedKey} />
          )}
          <DistributionStrip forecast={forecast} brandBlue={brandBlue} brandSoft={brandSoft} />
          <NarrativeBlock
            narrative={narrative}
            loading={narrativeLoading}
            brandBlue={brandBlue}
            brandSoft={brandSoft}
          />
        </div>

        {/* ----- Right column: two-tab sliders ----- */}
        <div className="lg:col-span-5">
          <SliderDeck
            stage={stage}
            sliders={sliders}
            onBarChange={onBarChange}
            brandBlue={brandBlue}
            brandSoft={brandSoft}
          />
        </div>
      </div>
      )}

      {/* Excel preview drawer */}
      <HistorySheet open={sheetDrawerOpen} onOpenChange={setSheetDrawerOpen} brandBlue={brandBlue} />
      {/* Funnel drill drawer */}
      <FunnelDrillDialog state={drillState} onClose={() => setDrillState(null)} brandBlue={brandBlue} />
    </div>
  );
}


// ============================================================================
// Sub-components
// ============================================================================

function HistoryTile({ counts, onOpen, brandBlue }) {   // eslint-disable-line no-unused-vars
  // Deprecated — kept for a possible compact variant; the live
  // DataSourcesStrip has replaced this in the panel header.
  const total = counts?.total_rows;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex items-center gap-2.5 h-9 px-3 rounded-lg border transition-colors hover:opacity-90"
      style={{ borderColor: "#E2E8F0", backgroundColor: "#F8FAFC" }}
    >
      <FileSpreadsheet size={14} style={{ color: brandBlue }} />
      <div className="text-left leading-tight">
        <div className="text-[11px] font-semibold text-slate-900">Chevron lifecycle history</div>
        <div className="text-[10px] text-slate-500">
          3 sheets · {total ? `${total.toLocaleString()} rows` : "loading…"}
        </div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Live data-source strip — shows the enterprise connectors feeding the Excel
// (Workday · ServiceNow CMDB · Nexthink · Ariba) with status dot + sync time
// + record count. Clicking any card opens the consolidated feed drawer.
// ---------------------------------------------------------------------------
const SOURCE_ICONS = {
  workday_hr:    { glyph: "W", color: "#F38B00" },
  workday_hiring:{ glyph: "W", color: "#F38B00" },
  sn_cmdb:       { glyph: "SN", color: "#293E40" },
  nexthink_dex:  { glyph: "NX", color: "#1D4ED8" },
  ariba_erp:     { glyph: "A",  color: "#0F766E" },
};
const STATUS_COLOR = { live: "#10B981", cached: "#F59E0B", offline: "#EF4444" };

function timeAgo(iso) {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.max(0, Math.floor(diffMs / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function DataSourcesStrip({ sources, counts, onOpen, resampling, connected, collectReady, onConnect, brandBlue, brandSoft }) {
  const list = sources?.connectors || [];
  const liveCount = list.filter((c) => c.status === "live").length;
  const total = counts?.total_rows;

  // Sequential pulse-in: each chip arrives one at a time (120 ms spacing)
  // so the panel feels like it's connecting to historical sources. Only
  // kicks off once the presenter has clicked "Connect sources"; before that
  // the chips sit in a dim locked state.
  const [revealed, setRevealed] = useState(0);
  useEffect(() => {
    if (!connected || !list.length) return;
    setRevealed(0);
    const timers = [];
    for (let i = 0; i < list.length; i++) {
      timers.push(setTimeout(() => setRevealed((r) => Math.max(r, i + 1)), 120 * (i + 1)));
    }
    return () => timers.forEach(clearTimeout);
  }, [connected, list.length]);

  const statusLine = !connected
    ? (collectReady ? "Collect done · click Run agent" : "sources not connected · waiting for Collect")
    : (list.length
        ? `${revealed < list.length ? `connecting ${revealed}/${list.length}` : `${liveCount} of ${list.length} connected`}`
        : "connecting…");

  return (
    <div
      className="rounded-xl p-3.5 flex items-center gap-3 flex-wrap"
      style={{ border: `1px solid ${brandSoft}`, backgroundColor: "#F8FBFF" }}
      data-testid="prob-data-sources-strip"
    >
      <div className="flex items-center gap-2 pr-3 border-r border-slate-200">
        <div className="relative">
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: connected ? STATUS_COLOR.live : "#CBD5E1" }}
          />
          {connected && (
            <div
              className="absolute inset-0 rounded-full animate-ping"
              style={{ backgroundColor: STATUS_COLOR.live, opacity: 0.4 }}
            />
          )}
        </div>
        <div className="leading-tight">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Historical sources</div>
          <div className="text-[10.5px] text-slate-500">
            {statusLine}
            {connected && total ? ` · ${total.toLocaleString()} records` : ""}
          </div>
        </div>
      </div>

      {list.length === 0 && (
        <div className="text-[11px] text-slate-400 italic">loading connector feed…</div>
      )}

      <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
        {list.map((c, idx) => {
          const icon = SOURCE_ICONS[c.id] || { glyph: "?", color: "#64748B" };
          const isLive = connected && idx < revealed;
          return (
            <button
              key={c.id}
              type="button"
              onClick={connected ? onOpen : onConnect}
              data-testid={`prob-source-${c.id}`}
              className="group flex items-center gap-2 h-11 px-2.5 rounded-lg border transition-all hover:bg-white hover:shadow-sm"
              style={{
                borderColor: "#E2E8F0",
                backgroundColor: "#FFFFFF",
                opacity: connected ? (isLive ? 1 : 0.35) : 0.4,
                transform: isLive ? "translateY(0)" : "translateY(2px)",
                transition: "opacity 240ms ease-out, transform 240ms ease-out",
              }}
              title={`${c.label} · ${c.detail}`}
            >
              <div
                className="w-7 h-7 rounded-md grid place-items-center text-[10px] font-bold text-white shrink-0"
                style={{ backgroundColor: connected ? icon.color : "#94A3B8" }}
              >
                {icon.glyph}
              </div>
              <div className="text-left leading-tight pr-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] font-bold text-slate-900">{c.label}</span>
                  {!isLive && connected ? (
                    <Loader2 size={9} className="animate-spin text-slate-400" />
                  ) : isLive ? (
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: STATUS_COLOR[c.status] || STATUS_COLOR.cached }}
                    />
                  ) : (
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                  )}
                </div>
                <div className="text-[9.5px] text-slate-500 font-mono">
                  {!connected
                    ? "not connected"
                    : isLive
                    ? `${c.records?.toLocaleString?.() || c.records} rec · ${timeAgo(c.last_sync)}`
                    : "reading…"}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Resampling ticker — brief Monte-Carlo recompute badge on slider commit */}
      {resampling && connected && (
        <span
          className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full shrink-0"
          style={{ backgroundColor: `${brandBlue}14`, color: brandBlue, animation: "psFade 500ms ease-out both" }}
          data-testid="prob-resample-ticker"
        >
          <Loader2 size={10} className="animate-spin" />
          running 10,000 paths
        </span>
      )}

      {!connected ? (
        <button
          type="button"
          onClick={collectReady ? onConnect : undefined}
          disabled={!collectReady}
          data-testid="prob-connect-sources-btn"
          className="h-9 px-3 rounded-lg text-[11px] font-semibold text-white transition-opacity shrink-0 inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
          style={{ backgroundColor: brandBlue, animation: collectReady ? "pulseAttract 2s infinite" : undefined }}
        >
          {collectReady ? <Sparkles size={12} /> : <Zap size={12} />}
          {collectReady ? "Run agent" : "Waiting for Collect"}
          <style>{`@keyframes pulseAttract { 0%,100%{box-shadow:0 0 0 0 ${brandBlue}44;} 50%{box-shadow:0 0 0 6px ${brandBlue}00;} }`}</style>
        </button>
      ) : (
        <button
          type="button"
          onClick={onOpen}
          data-testid="prob-history-tile"
          className="h-9 px-3 rounded-lg text-[11px] font-semibold text-white hover:opacity-90 transition-opacity shrink-0"
          style={{ backgroundColor: brandBlue }}
        >
          <span className="inline-flex items-center gap-1.5">
            <FileSpreadsheet size={12} />
            View consolidated feed
          </span>
        </button>
      )}
      <style>{`@keyframes psFade { from{opacity:0;transform:translateY(-2px);} to{opacity:1;transform:translateY(0);} }`}</style>
    </div>
  );
}

// Locked placeholder shown in place of the chart until "Run agent" is clicked.
// Copy adapts to whether the Collect step has already finished:
//   - collectReady=false  → "Connect sources first in the Collect step above"
//   - collectReady=true   → "Run agent to build the forecast + reasoning"
function ConnectSourcesPlaceholder({ onConnect, collectReady, brandBlue, brandSoft }) {
  const ready = !!collectReady;
  return (
    <div
      className="rounded-xl border-2 border-dashed p-10 mt-5 flex flex-col items-center justify-center text-center gap-3"
      style={{ borderColor: `${brandBlue}44`, backgroundColor: "#F8FBFF" }}
      data-testid="prob-chart-locked"
    >
      <div
        className="w-11 h-11 rounded-full grid place-items-center"
        style={{ backgroundColor: `${brandBlue}14`, color: brandBlue }}
      >
        {ready ? <Sparkles size={18} /> : <Zap size={18} />}
      </div>
      <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-slate-500">
        Scenario Planner · forecast pending
      </div>
      <div className="text-[13px] text-slate-700 max-w-md">
        {ready
          ? "Sources collected. Click Run agent to compute the P10 / P50 / P90 bands, show the reasoning trace and settle the decision."
          : "Connect the enterprise sources in the Collect step above first. Once the connectors return, the forecast + reasoning can run from this panel."}
      </div>
      <Button
        onClick={onConnect}
        disabled={!ready}
        data-testid="prob-chart-locked-cta"
        className="mt-1 text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ backgroundColor: brandBlue, animation: ready ? "pulseRun 2s infinite" : undefined }}
      >
        {ready ? <Sparkles size={12} className="mr-1.5" /> : <Zap size={12} className="mr-1.5" />}
        {ready ? "Run agent" : "Waiting for Collect"}
      </Button>
      <style>{`@keyframes pulseRun { 0%,100%{box-shadow:0 0 0 0 ${brandBlue}44;} 50%{box-shadow:0 0 0 8px ${brandBlue}00;} }`}</style>
    </div>
  );
}

// Freshness line — provenance signal. One muted row under the connector strip
// that reads: "history refreshed · N connectors · M records · 10k paths · Dec 7 anchor".
function FreshnessLine({ sources, counts, paths, brandBlue }) {
  const list = sources?.connectors || [];
  if (!list.length) return null;
  const liveCount = list.filter((c) => c.status === "live").length;
  const total = counts?.total_rows;
  // Use the most-recent connector sync as "history refreshed" timestamp.
  const latestSync = list
    .map((c) => c.last_sync)
    .filter(Boolean)
    .sort()
    .slice(-1)[0];
  return (
    <div
      className="mt-2 px-3 py-1.5 flex items-center gap-2 flex-wrap text-[10.5px] text-slate-500"
      data-testid="prob-freshness-line"
    >
      <span className="inline-flex items-center gap-1.5">
        <span className="w-1 h-1 rounded-full animate-pulse" style={{ backgroundColor: brandBlue }} />
        <span className="font-semibold text-slate-600">history refreshed {timeAgo(latestSync)}</span>
      </span>
      <span className="text-slate-300">·</span>
      <span>{liveCount}/{list.length} connectors</span>
      <span className="text-slate-300">·</span>
      {total && <span className="tabular-nums">{total.toLocaleString()} records</span>}
      {total && <span className="text-slate-300">·</span>}
      <span className="tabular-nums">{(paths || 10000).toLocaleString()} Monte-Carlo paths</span>
      <span className="text-slate-300">·</span>
      <span>Dec 7, 2026 anchor</span>
    </div>
  );
}

function HeadlineForecast({ forecast, loading, unit, brandBlue, brandSoft, agentRunning, consumedKey }) {
  const p50 = forecast?.p50 ?? "—";
  const p10 = forecast?.p10 ?? "—";
  const p90 = forecast?.p90 ?? "—";
  const conf = forecast?.confidence ?? "—";
  return (
    <div
      className="rounded-xl p-5 flex items-center justify-between gap-5 transition-all"
      style={{
        border: `1px solid ${agentRunning ? brandBlue : brandSoft}`,
        backgroundColor: "#F8FBFF",
        boxShadow: agentRunning ? `0 0 0 3px ${brandBlue}22` : "none",
      }}
    >
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
          P50 · {agentRunning ? "agent reading…" : (loading ? "updating…" : "committed")}
        </div>
        <div className="flex items-baseline gap-2">
          <span
            key={`p50-${consumedKey}`}
            className="text-5xl font-bold tracking-tight"
            style={{
              color: brandBlue,
              animation: consumedKey > 0 ? "pfConsumed 650ms ease-out" : undefined,
            }}
            data-testid="prob-p50"
          >
            {p50}
          </span>
          <span className="text-[13px] text-slate-500">{unit}</span>
          {agentRunning && (
            <Loader2 size={14} className="animate-spin ml-1" style={{ color: brandBlue }} />
          )}
        </div>
        <div className="text-[12px] text-slate-600 mt-1.5">
          <span className="font-semibold">P10 {p10}</span>
          <span className="text-slate-400 mx-2">·</span>
          <span className="font-semibold">P90 {p90}</span>
        </div>
      </div>
      <ConfidenceRing value={typeof conf === "number" ? conf : 0} brandBlue={brandBlue} />
      <style>{`
        @keyframes pfConsumed { 0%{transform:scale(1);} 40%{transform:scale(1.14);text-shadow:0 0 12px ${brandBlue}66;} 100%{transform:scale(1);} }
      `}</style>
    </div>
  );
}

function ConfidenceRing({ value, brandBlue }) {
  const r = 26;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  const dash = (pct / 100) * c;
  return (
    <div className="relative shrink-0" title={`${pct}% confidence`}>
      <svg width="68" height="68" viewBox="0 0 68 68" data-testid="prob-confidence-ring">
        <circle cx="34" cy="34" r={r} fill="none" stroke="#E2E8F0" strokeWidth="6" />
        <circle
          cx="34" cy="34" r={r}
          fill="none"
          stroke={brandBlue}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          transform="rotate(-90 34 34)"
          style={{ transition: "stroke-dasharray 220ms ease-out" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-tight">
        <div className="text-[14px] font-bold text-slate-900">{pct}%</div>
        <div className="text-[8px] text-slate-500 uppercase font-semibold tracking-wider">conf</div>
      </div>
    </div>
  );
}

function DemandFunnel({ forecast, loading, onDrill, brandBlue, brandSoft, agentRunning, consumedKey }) {
  const stages = forecast?.funnel?.stages || [];
  const confidence = forecast?.confidence;
  const p10 = forecast?.p10;
  const p90 = forecast?.p90;
  const p50 = forecast?.p50;
  const equation = forecast?.funnel?.equation;

  // Bar heights scale 100 → ~55 so the funnel visibly contracts right-ward.
  const maxV = Math.max(1, ...stages.map((s) => s.value));
  return (
    <div
      className="rounded-xl p-5 transition-all"
      style={{
        border: `1px solid ${agentRunning ? brandBlue : brandSoft}`,
        backgroundColor: "#F8FBFF",
        boxShadow: agentRunning ? `0 0 0 3px ${brandBlue}22` : "none",
      }}
      data-testid="prob-demand-funnel"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
          Workday Joiner Funnel
          {agentRunning && (
            <span className="inline-flex items-center gap-1 text-[9.5px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${brandBlue}1A`, color: brandBlue }}>
              <Loader2 size={8} className="animate-spin" /> AGENT READING
            </span>
          )}
        </div>
        <div className="text-[11px] font-mono text-slate-500">{equation}</div>
      </div>
      <div className="grid grid-cols-5 gap-2 items-end" style={{ height: 220 }}>
        {stages.map((s, i) => {
          const isNet = s.id === "net";
          const BAR_MAX = 150;
          const h = Math.max(22, (s.value / maxV) * BAR_MAX);
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onDrill(s.id)}
              data-testid={`prob-funnel-${s.id}`}
              className="relative flex flex-col items-stretch group focus:outline-none"
              style={{ height: 220 }}
            >
              {/* Label row — fixed ~44px above the bar so it never overlaps */}
              <div className="flex flex-col items-center justify-end" style={{ height: 220 - BAR_MAX }}>
                <div className="text-[18px] font-bold leading-none" style={{ color: isNet ? brandBlue : "#334155" }}>
                  {s.value}
                </div>
                {s.operator && (
                  <div className="text-[10px] font-mono text-slate-500 mt-0.5">{s.operator}</div>
                )}
              </div>
              {/* Bar area */}
              <div className="relative flex items-end" style={{ height: BAR_MAX }}>
                <div
                  className="w-full rounded-t-md transition-all"
                  style={{
                    height: `${h}px`,
                    backgroundColor: isNet ? brandBlue : `${brandBlue}CC`,
                    opacity: loading ? 0.5 : 1,
                    boxShadow: isNet ? `0 2px 0 ${brandBlue}` : "none",
                  }}
                />
                {/* P10/P90 overlay on NET */}
                {isNet && p10 != null && p90 != null && (
                  <div className="absolute inset-x-0 bottom-0 pointer-events-none" style={{ height: `${h}px` }}>
                    <div
                      className="absolute left-0 right-0 border-dashed"
                      style={{ bottom: `${(p90 / maxV) * BAR_MAX}px`, borderTopWidth: 1, borderColor: "#fff" }}
                    />
                    <div
                      className="absolute left-0 right-0 border-dashed"
                      style={{ bottom: `${(p10 / maxV) * BAR_MAX}px`, borderTopWidth: 1, borderColor: "#fff" }}
                    />
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-5 gap-2 mt-2">
        {stages.map((s) => (
          <div key={`l-${s.id}`} className="text-center">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-600 leading-tight">{s.label}</div>
          </div>
        ))}
      </div>
      {/* Headline range + confidence */}
      <div className="mt-4 flex items-center justify-between gap-4 border-t pt-3" style={{ borderColor: brandSoft }}>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Workday Joiners · P50</div>
          <div className="flex items-baseline gap-2">
            <span
              key={`p50-${consumedKey}`}
              className="text-4xl font-bold tracking-tight"
              style={{
                color: brandBlue,
                animation: consumedKey > 0 ? "pfConsumedFn 650ms ease-out" : undefined,
              }}
              data-testid="prob-p50"
            >
              {p50 ?? "—"}
            </span>
            <span className="text-[13px] text-slate-500">joiners</span>
          </div>
          <div className="text-[12px] text-slate-600 mt-0.5">
            <span className="font-semibold">P10 {p10}</span>
            <span className="text-slate-400 mx-2">·</span>
            <span className="font-semibold">P90 {p90}</span>
          </div>
        </div>
        <ConfidenceRing value={confidence || 0} brandBlue={brandBlue} />
      </div>
      <style>{`
        @keyframes pfConsumedFn { 0%{transform:scale(1);} 40%{transform:scale(1.14);text-shadow:0 0 12px ${brandBlue}66;} 100%{transform:scale(1);} }
      `}</style>
    </div>
  );
}

function DistributionStrip({ forecast, brandBlue, brandSoft }) {
  const bins = forecast?.distribution || [];
  const p10 = forecast?.p10;
  const p50 = forecast?.p50;
  const p90 = forecast?.p90;
  if (!bins.length) return null;
  const w = 100 / bins.length;
  // Map P10/P50/P90 onto bin positions proportionally.
  const minV = Math.min(p10, p50 * 0.55);
  const maxV = Math.max(p90, p50 * 1.6);
  const range = Math.max(1, maxV - minV);
  const posPct = (v) => Math.max(0, Math.min(100, ((v - minV) / range) * 100));
  return (
    <div
      className="rounded-xl px-4 py-3"
      style={{ border: `1px solid ${brandSoft}`, backgroundColor: "#FFFFFF" }}
      data-testid="prob-distribution-strip"
    >
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Distribution · bootstrap n = 3000</div>
      <div className="relative h-14">
        <svg viewBox="0 0 100 48" preserveAspectRatio="none" style={{ width: "100%", height: "100%" }}>
          {bins.map((h, i) => (
            <rect
              key={i}
              x={i * w + 0.3}
              y={48 - (h * 0.44)}
              width={w - 0.6}
              height={Math.max(1, h * 0.44)}
              fill={brandBlue}
              fillOpacity={0.35}
            />
          ))}
          {/* P10 / P90 ticks */}
          <line x1={posPct(p10)} x2={posPct(p10)} y1={0} y2={48} stroke={brandBlue} strokeWidth="0.5" strokeDasharray="2 2" opacity="0.75" />
          <line x1={posPct(p90)} x2={posPct(p90)} y1={0} y2={48} stroke={brandBlue} strokeWidth="0.5" strokeDasharray="2 2" opacity="0.75" />
          {/* P50 bold line */}
          <line x1={posPct(p50)} x2={posPct(p50)} y1={0} y2={48} stroke={brandBlue} strokeWidth="1.2" />
        </svg>
        <div className="absolute inset-x-0 bottom-0 flex justify-between text-[9px] font-mono text-slate-500">
          <span>P10 {p10}</span>
          <span>P50 {p50}</span>
          <span>P90 {p90}</span>
        </div>
      </div>
    </div>
  );
}

function NarrativeBlock({ narrative, loading, brandBlue, brandSoft }) {
  return (
    <div
      className="rounded-xl px-4 py-3.5"
      style={{ border: `1px solid ${brandSoft}`, backgroundColor: "#FEFCE8" }}
      data-testid="prob-narrative"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Sparkles size={12} style={{ color: brandBlue }} />
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Agent reasoning · why this P50</div>
        {loading && <Loader2 size={11} className="animate-spin text-slate-400" />}
      </div>
      <p className="text-[13px] text-slate-700 leading-relaxed">
        {narrative || (loading ? "Thinking…" : "Drag a slider to generate reasoning.")}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Slider deck · 2 tabs
// ---------------------------------------------------------------------------
function SliderDeck({ stage, sliders, onBarChange, brandBlue, brandSoft }) {
  const [tab, setTab] = useState("business");
  // Per-stage slider lists: Refresh gets device-side knobs; Recovery gets
  // return-pool knobs; Demand gets HR-side knobs. Historical tab shows the
  // long-run drivers that are meaningful for the active agent.
  const businessKeys   = Object.keys(SLIDER_META).filter((k) => SLIDER_META[k].tab === "business"   && SLIDER_META[k].agents.includes(stage));
  const historicalKeys = Object.keys(SLIDER_META).filter((k) => SLIDER_META[k].tab === "historical" && SLIDER_META[k].agents.includes(stage));
  const gridCols = (keys) => keys.length <= 3 ? "grid-cols-3" : keys.length === 4 ? "grid-cols-4" : "grid-cols-5";

  return (
    <div
      className="rounded-xl p-4 h-full"
      style={{ border: `1px solid ${brandSoft}`, backgroundColor: "#FFFFFF" }}
    >
      <Tabs value={tab} onValueChange={setTab} className="w-full" data-testid="prob-slider-tabs">
        <TabsList className="grid w-full grid-cols-2 h-9 bg-slate-100">
          <TabsTrigger value="business" className="text-[11.5px] font-semibold">Business levers</TabsTrigger>
          <TabsTrigger value="historical" className="text-[11.5px] font-semibold">Historical</TabsTrigger>
        </TabsList>

        <TabsContent value="business" className="mt-4">
          <div className={`grid ${gridCols(businessKeys)} gap-3`} style={{ minHeight: 220 }}>
            {businessKeys.map((k) => (
              <SliderBar
                key={k}
                sliderKey={k}
                value={sliders[k] ?? SLIDER_META[k].bl}
                onChange={(v) => onBarChange(k, v)}
                relevant={true}
                brandBlue={brandBlue}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="historical" className="mt-4">
          <div className={`grid ${gridCols(historicalKeys)} gap-3`} style={{ minHeight: 220 }}>
            {historicalKeys.map((k) => (
              <SliderBar
                key={k}
                sliderKey={k}
                value={sliders[k] ?? SLIDER_META[k].bl}
                onChange={(v) => onBarChange(k, v)}
                relevant={true}
                brandBlue={brandBlue}
              />
            ))}
          </div>
        </TabsContent>
      </Tabs>
      <div className="text-[10px] text-slate-500 mt-3 leading-4">
        Drag any bar · P10 / P50 / P90 re-render in real time.
        Shared knobs (growth, attrition) cascade across the forecasting agents.
      </div>
    </div>
  );
}

function SliderBar({ sliderKey, value, onChange, relevant, brandBlue }) {
  const meta = SLIDER_META[sliderKey];
  const barRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const pct = (Number(value) - meta.min) / (meta.max - meta.min);
  const fillPct = Math.max(0, Math.min(1, pct)) * 100;
  const isBaseline = Math.abs(Number(value) - meta.bl) < 0.001;

  const commitFromEvent = (e) => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect) return;
    const clientY = (e.touches ? e.touches[0].clientY : e.clientY);
    const localY = clientY - rect.top;
    const invert = 1 - Math.max(0, Math.min(1, localY / rect.height));
    const raw = meta.min + invert * (meta.max - meta.min);
    const stepped = Math.round(raw / meta.step) * meta.step;
    const bounded = Math.max(meta.min, Math.min(meta.max, stepped));
    onChange(Number(bounded.toFixed(meta.step < 1 ? 2 : 0)));
  };

  const handleDown = (e) => {
    e.preventDefault();
    setDragging(true);
    commitFromEvent(e);
  };
  useEffect(() => {
    if (!dragging) return;
    const move = (e) => commitFromEvent(e);
    const up = () => setDragging(false);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging]);

  return (
    <div className="flex flex-col items-center" data-testid={`prob-slider-${sliderKey}`}>
      {/* Pill with current value */}
      <div
        className="text-[11px] font-bold px-2 py-0.5 rounded-full mb-1.5"
        style={{
          backgroundColor: relevant ? brandBlue : "#E2E8F0",
          color: relevant ? "#FFFFFF" : "#64748B",
          opacity: relevant ? 1 : 0.6,
        }}
      >
        {meta.step < 1 ? Number(value).toFixed(2) : Math.round(Number(value))}{meta.unit !== "×" ? meta.unit : ""}
      </div>
      {/* Bar */}
      <div
        ref={barRef}
        onMouseDown={handleDown}
        onTouchStart={handleDown}
        className="relative w-full rounded-md cursor-ns-resize select-none"
        style={{
          height: 150,
          backgroundColor: "#F1F5F9",
          border: `1px solid #E2E8F0`,
          opacity: relevant ? 1 : 0.5,
        }}
      >
        {/* Baseline tick */}
        <div
          className="absolute left-0 right-0 h-px"
          style={{
            bottom: `${((meta.bl - meta.min) / (meta.max - meta.min)) * 100}%`,
            backgroundColor: "#94A3B8",
          }}
          title={`Baseline ${meta.bl}`}
        />
        {/* Fill */}
        <div
          className="absolute left-0 right-0 bottom-0 rounded-b-md transition-all"
          style={{
            height: `${fillPct}%`,
            backgroundColor: brandBlue,
            opacity: isBaseline ? 0.6 : 0.85,
          }}
        />
      </div>
      <div className="text-[9.5px] font-semibold text-slate-600 mt-1.5 text-center leading-tight uppercase tracking-wide">
        {meta.label}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Excel preview drawer
// ---------------------------------------------------------------------------
function HistorySheet({ open, onOpenChange, brandBlue }) {
  const [preview, setPreview] = useState(null);
  const [sources, setSources] = useState(null);
  const [tab, setTab] = useState("A");
  useEffect(() => {
    if (!open) return;
    if (!preview) endpoints.probExcelPreview().then(setPreview).catch(() => {});
    if (!sources) endpoints.probDataSources().then(setSources).catch(() => {});
  }, [open, preview, sources]);

  const download = async () => {
    const token = localStorage.getItem("flexspace_auth_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/lifecycle/probabilistic/excel/download`;
    const resp = await axios.get(url, {
      responseType: "blob",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const blob = new Blob([resp.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "chevron_lifecycle_history.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const sheets = preview ? {
    A: preview.sheet_a, B: preview.sheet_b, C: preview.sheet_c,
  } : null;
  const titles = preview?.sheet_names || ["A", "B", "C"];
  const activeSheet = sheets ? (tab === "A" ? sheets.A : tab === "B" ? sheets.B : sheets.C) : null;

  // Connectors feeding the current sheet.
  const activeConnectorIds = sources?.sheet_sources?.[tab]?.connectors || [];
  const activeConnectors = (sources?.connectors || []).filter((c) => activeConnectorIds.includes(c.id));
  const sheetLabel = sources?.sheet_sources?.[tab]?.label;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-hidden flex flex-col p-0" data-testid="prob-history-drawer">
        <SheetHeader className="p-6 pb-4 border-b border-slate-200">
          <SheetTitle className="flex items-center gap-2">
            <FileSpreadsheet size={16} style={{ color: brandBlue }} />
            Consolidated lifecycle feed
          </SheetTitle>
          <SheetDescription>
            Materialised view of {sources?.connectors?.length || "—"} enterprise connectors — streaming into the P10 / P50 / P90 model.
          </SheetDescription>

          {/* Connected sources summary */}
          {sources && (
            <div className="pt-3 grid grid-cols-2 lg:grid-cols-3 gap-2">
              {sources.connectors.map((c) => {
                const icon = SOURCE_ICONS[c.id] || { glyph: "?", color: "#64748B" };
                return (
                  <div
                    key={c.id}
                    className="flex items-center gap-2 px-2.5 py-2 rounded-lg border"
                    style={{ borderColor: "#E2E8F0", backgroundColor: "#F8FBFF" }}
                    data-testid={`prob-drawer-source-${c.id}`}
                  >
                    <div
                      className="w-7 h-7 rounded-md grid place-items-center text-[10px] font-bold text-white shrink-0"
                      style={{ backgroundColor: icon.color }}
                    >
                      {icon.glyph}
                    </div>
                    <div className="leading-tight min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[11px] font-bold text-slate-900 truncate">{c.label}</span>
                        <span
                          className="w-1.5 h-1.5 rounded-full shrink-0"
                          style={{ backgroundColor: STATUS_COLOR[c.status] || STATUS_COLOR.cached }}
                          title={c.status}
                        />
                      </div>
                      <div className="text-[9.5px] text-slate-500 font-mono truncate">
                        {c.records?.toLocaleString?.() || c.records} rec · {timeAgo(c.last_sync)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex items-center gap-2 pt-2">
            <Button
              onClick={download}
              size="sm"
              className="text-white hover:opacity-90"
              style={{ backgroundColor: brandBlue }}
              data-testid="prob-history-download"
            >
              <Download size={12} className="mr-1.5" />
              Download .xlsx
            </Button>
            <div className="flex items-center gap-1 ml-2">
              {["A", "B", "C"].map((k, i) => (
                <button
                  key={k}
                  onClick={() => setTab(k)}
                  data-testid={`prob-history-tab-${k}`}
                  className="text-[11px] font-semibold h-8 px-3 rounded-md"
                  style={tab === k
                    ? { backgroundColor: brandBlue, color: "#fff" }
                    : { color: "#64748B", border: "1px solid #E2E8F0" }}
                >
                  {titles[i] || k}
                </button>
              ))}
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-auto p-6 pt-4">
          {!preview && <div className="text-sm text-slate-500">Loading preview…</div>}
          {preview && activeSheet && (
            <div>
              {/* Sheet source attribution */}
              {activeConnectors.length > 0 && (
                <div
                  className="rounded-lg px-3 py-2 mb-3 flex items-center gap-2 flex-wrap"
                  style={{ border: `1px solid ${brandBlue}22`, backgroundColor: `${brandBlue}08` }}
                >
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: brandBlue }}>
                    Sourced from
                  </span>
                  {activeConnectors.map((c, i) => {
                    const icon = SOURCE_ICONS[c.id] || { glyph: "?", color: "#64748B" };
                    return (
                      <span key={c.id} className="inline-flex items-center gap-1.5">
                        <span
                          className="w-4 h-4 rounded grid place-items-center text-[8px] font-bold text-white"
                          style={{ backgroundColor: icon.color }}
                        >
                          {icon.glyph}
                        </span>
                        <span className="text-[11px] font-semibold text-slate-700">{c.label}</span>
                        {i < activeConnectors.length - 1 && <span className="text-slate-400">+</span>}
                      </span>
                    );
                  })}
                  <span className="text-[10px] text-slate-500 ml-auto">
                    {sheetLabel || ""} · {activeSheet.total_rows.toLocaleString()} rows
                  </span>
                </div>
              )}
              <div className="text-[11px] text-slate-500 mb-2">
                Showing {activeSheet.rows.length} of {activeSheet.total_rows.toLocaleString()} rows
              </div>
              <div className="overflow-auto border rounded-lg">
                <table className="w-full text-[11.5px]">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      {activeSheet.headers.map((h) => (
                        <th key={h} className="text-left px-2.5 py-1.5 font-bold uppercase tracking-wider text-slate-600 text-[10px] border-b whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activeSheet.rows.map((r, i) => (
                      <tr key={i} className="hover:bg-slate-50 border-b">
                        {activeSheet.headers.map((h) => (
                          <td key={h} className="px-2.5 py-1.5 text-slate-700 whitespace-nowrap">{String(r[h] ?? "")}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function FunnelDrillDialog({ state, onClose, brandBlue }) {
  const open = !!state;
  const d = state?.data;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="prob-funnel-drill">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: brandBlue }}>
            <FileSpreadsheet size={14} />
            {d?.title || "Backing rows"}
          </DialogTitle>
          <DialogDescription>
            Rows from the Chevron history that drive this funnel stage.
          </DialogDescription>
        </DialogHeader>
        {d && (
          <div className="overflow-auto border rounded-lg max-h-[55vh]">
            <table className="w-full text-[11.5px]">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  {d.headers.map((h) => (
                    <th key={h} className="text-left px-2.5 py-1.5 font-bold uppercase tracking-wider text-slate-600 text-[10px] border-b whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.rows.map((r, i) => (
                  <tr key={i} className="hover:bg-slate-50 border-b">
                    {d.headers.map((h) => (
                      <td key={h} className="px-2.5 py-1.5 text-slate-700 whitespace-nowrap">{String(r[h] ?? "")}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Baseline sliders helper for callers.
export const BASELINE_SLIDERS = Object.fromEntries(
  Object.entries(SLIDER_META).map(([k, v]) => [k, v.bl])
);
